(function () {
  'use strict';

  // Variant-specific household quantity limits.
  // Built on the v13 implementation, with the product Add to Cart gate and
  // Dawn pub/sub cart updates tightened up. Disclaimer code is untouched.
  const PRODUCT_DATA_SELECTOR = '[data-product-max-quantity-variants]';
  const PRODUCT_QUANTITY_SELECTOR = '.product-form__quantity quantity-input .quantity__input[name="quantity"]';
  const CART_QUANTITY_SELECTOR = 'quantity-input .quantity__input[data-quantity-variant-id]';
  const PLUS_SELECTOR = 'quantity-input .quantity__button[name="plus"]';
  const NOTICE_CLASS = 'variant-max-quantity-notice';
  const PRODUCT_FORM_SELECTOR = '.product-form form[data-type="add-to-cart-form"]';
  const PRODUCT_SUBMIT_SELECTOR = '.product-form__submit';

  let productVariants = [];
  const noticedProductVariants = new Set();
  const noticedCartLines = new Set();
  let cartFetchPromise = null;
  let lastCartQuantities = {};
  let listenersBound = false;

  function parseLimit(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function loadProductVariants() {
    const node = document.querySelector(PRODUCT_DATA_SELECTOR);
    if (!node) { productVariants = []; return; }
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      productVariants = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Could not parse variant max quantity data', e);
      productVariants = [];
    }
  }

  function selectedVariantId() {
    const input = document.querySelector('.product-variant-id');
    return input ? String(input.value) : null;
  }

  function productLimit(id = selectedVariantId()) {
    const variant = productVariants.find(v => String(v.id) === String(id));
    return parseLimit(variant && variant.maxQuantity);
  }

  function setCartQuantitiesFromCart(cart) {
    const quantities = {};
    (cart?.items || []).forEach(item => {
      const id = String(item.variant_id);
      quantities[id] = (quantities[id] || 0) + Number(item.quantity || 0);
    });
    lastCartQuantities = quantities;
    return quantities;
  }

  function getCartQuantities() {
    if (cartFetchPromise) return cartFetchPromise;
    cartFetchPromise = fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Cart fetch failed')))
      .then(setCartQuantitiesFromCart)
      .catch(() => lastCartQuantities)
      .finally(() => { cartFetchPromise = null; });
    return cartFetchPromise;
  }

  function productRemaining(id = selectedVariantId()) {
    const limit = productLimit(id);
    if (limit === null) return null;
    return Math.max(0, limit - (lastCartQuantities[String(id)] || 0));
  }

  function showNotice(host, limit) {
    if (!host) return;
    let el = host.querySelector(':scope > .' + NOTICE_CLASS);
    if (!el) {
      el = document.createElement('div');
      el.className = NOTICE_CLASS;
      el.setAttribute('role', 'status');
      host.appendChild(el);
    }
    el.textContent = `Limited to ${limit} copies per household.`;
    el.hidden = false;
  }

  function hideNotice(host) {
    const el = host?.querySelector(':scope > .' + NOTICE_CLASS);
    if (el) el.hidden = true;
  }

  function productInputChanged(input) {
    const limit = productLimit();
    const host = input.closest('.product-form__quantity');
    const button = document.querySelector(PRODUCT_FORM_SELECTOR + ' ' + PRODUCT_SUBMIT_SELECTOR);
    if (limit === null) {
      input.removeAttribute('data-custom-max');
      input.removeAttribute('max');
      hideNotice(host);
      if (button?.dataset.householdLimitDisabled === 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        delete button.dataset.householdLimitDisabled;
      }
      return;
    }

    const variantId = selectedVariantId();
    const inCart = lastCartQuantities[variantId] || 0;
    const remaining = Math.max(0, limit - inCart);
    const current = Number(input.value);

    if (remaining > 0) {
      input.setAttribute('data-custom-max', String(remaining));
      input.setAttribute('max', String(remaining));
      if (!Number.isFinite(current) || current < 1) input.value = 1;
      else if (current > remaining) input.value = remaining;

      // Dawn's quantity-input calculated the button state before our custom
      // max was applied. Recalculate it now so the +/- cursor/state matches
      // the current remaining allowance immediately.
      input.closest('quantity-input')?.validateQtyRules();

      if (button?.dataset.householdLimitDisabled === 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        delete button.dataset.householdLimitDisabled;
      }
    } else {
      input.setAttribute('data-custom-max', '0');
      // Dawn's quantity component expects a usable positive quantity. Keep it
      // at 1, but disable the actual purchase button when no allowance remains.
      input.setAttribute('max', '1');
      input.value = 1;
      input.closest('quantity-input')?.validateQtyRules();
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.householdLimitDisabled = 'true';
      }
    }

    if (noticedProductVariants.has(variantId) || remaining <= 0) showNotice(host, limit);
  }

  function cartLimit(input) {
    return parseLimit(input.closest('.cart-item')?.getAttribute('data-max-quantity'));
  }

  function cartKey(input) {
    const row = input.closest('.cart-item');
    return row?.id || String(input.getAttribute('data-quantity-variant-id'));
  }

  function cartTotalForVariant(id) {
    let total = 0;
    document.querySelectorAll(CART_QUANTITY_SELECTOR).forEach(input => {
      if (String(input.getAttribute('data-quantity-variant-id')) === String(id)) {
        total += Number(input.value || 0);
      }
    });
    return total;
  }

  function cartInputChanged(input) {
    const limit = cartLimit(input);
    const cell = input.closest('.cart-item__quantity');
    if (limit === null) { hideNotice(cell); return; }

    const current = Number(input.value);
    if (!Number.isFinite(current)) return;

    // Keep Dawn's native button state synchronized with our household limit.
    // The actual total is still checked below by handlePlus/cartInputChanged.
    input.setAttribute('max', String(limit));
    input.closest('quantity-input')?.validateQtyRules();

    const id = input.getAttribute('data-quantity-variant-id');
    const otherQuantity = cartTotalForVariant(id) - current;
    const allowedForThisLine = Math.max(0, limit - otherQuantity);
    const key = cartKey(input);

    if (current > allowedForThisLine) {
      input.value = allowedForThisLine;
      noticedCartLines.add(key);
      showNotice(cell, limit);
    } else if (current < allowedForThisLine) {
      noticedCartLines.delete(key);
      hideNotice(cell);
    } else if (noticedCartLines.has(key)) {
      showNotice(cell, limit);
    }
  }

  function handlePlus(event) {
    const button = event.target.closest?.(PLUS_SELECTOR);
    if (!button) return;
    const input = button.parentElement?.querySelector('.quantity__input');
    if (!input) return;

    if (input.matches('[name="quantity"]') && input.closest('.product-form__quantity')) {
      const limit = productLimit();
      if (limit === null) return;

      const variantId = selectedVariantId();
      const inCart = lastCartQuantities[variantId] || 0;
      const remaining = Math.max(0, limit - inCart);
      const current = Number(input.value) || 0;

      if (remaining <= 0 || current >= remaining) {
        event.preventDefault();
        event.stopImmediatePropagation();
        input.value = Math.max(1, remaining || 1);
        input.setAttribute('max', String(Math.max(1, remaining || 1)));
        noticedProductVariants.add(variantId);
        showNotice(input.closest('.product-form__quantity'), limit);
      }
      return;
    }

    const limit = cartLimit(input);
    if (limit === null) return;
    const id = input.getAttribute('data-quantity-variant-id');
    const total = cartTotalForVariant(id);
    const current = Number(input.value) || 0;
    if (total >= limit || current >= Math.max(0, limit - (total - current))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      input.value = current;
      noticedCartLines.add(cartKey(input));
      showNotice(input.closest('.cart-item__quantity'), limit);
    }
  }

  function handleChange(event) {
    const input = event.target;
    if (!input.matches?.('.quantity__input')) return;
    if (input.matches('[name="quantity"]') && input.closest('.product-form__quantity')) productInputChanged(input);
    else if (input.matches('[data-quantity-variant-id]')) cartInputChanged(input);
  }

  function handleVariantChange(event) {
    if (!event.target.closest?.('variant-selects, variant-radios, .product-form')) return;
    setTimeout(() => {
      loadProductVariants();
      getCartQuantities().then(() => {
        const input = document.querySelector(PRODUCT_QUANTITY_SELECTOR);
        if (input) productInputChanged(input);
      });
    }, 0);
  }

  function refreshCartSide(event) {
    if (event?.cartData?.items) setCartQuantitiesFromCart(event.cartData);
    setTimeout(() => {
      loadProductVariants();
      document.querySelectorAll(CART_QUANTITY_SELECTOR).forEach(input => cartInputChanged(input));
      if (event?.cartData?.items) {
        const input = document.querySelector(PRODUCT_QUANTITY_SELECTOR);
        if (input) productInputChanged(input);
      } else {
        getCartQuantities().then(() => {
          const input = document.querySelector(PRODUCT_QUANTITY_SELECTOR);
          if (input) productInputChanged(input);
        });
      }
    }, 0);
  }

  function handleProductSubmit(event) {
    const form = event.target?.closest?.(PRODUCT_FORM_SELECTOR);
    if (!form) return;
    const input = form.querySelector(PRODUCT_QUANTITY_SELECTOR);
    if (!input) return;
    const limit = productLimit();
    if (limit === null) return;

    const remaining = productRemaining();
    const requested = Number(input.value) || 1;
    if (remaining <= 0 || requested > remaining) {
      event.preventDefault();
      event.stopImmediatePropagation();
      input.value = remaining > 0 ? remaining : 1;
      noticedProductVariants.add(selectedVariantId());
      showNotice(input.closest('.product-form__quantity'), limit);
      productInputChanged(input);
    }
  }

  function init() {
    loadProductVariants();
    getCartQuantities().then(() => {
      const input = document.querySelector(PRODUCT_QUANTITY_SELECTOR);
      if (input) productInputChanged(input);
      document.querySelectorAll(CART_QUANTITY_SELECTOR).forEach(input => cartInputChanged(input));
    });

    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener('click', handlePlus, true);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('change', handleVariantChange);
    document.addEventListener('submit', handleProductSubmit, true);

    // Dawn's actual product/cart updates use this pub/sub system. Use the
    // cartData supplied by Dawn instead of relying on non-existent DOM events.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartSide);
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        setTimeout(() => {
          loadProductVariants();
          getCartQuantities().then(() => {
            const input = document.querySelector(PRODUCT_QUANTITY_SELECTOR);
            if (input) productInputChanged(input);
          });
        }, 0);
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
